import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  VStack,
  Text,
  Image,
  Container,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Flex,
  Icon,
  HStack,
  Center,
  Tooltip,
  useColorModeValue,
  Badge,
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowBackIcon, CheckIcon, CopyIcon } from '@chakra-ui/icons';
import {
  MdOutlineWeb,
  MdDownload,
  MdVerified,
  MdQrCode2,
  MdPhone,
  MdEmail,
} from 'react-icons/md';
import { QRCodeCanvas } from 'qrcode.react';
import { storage, BusinessCard } from '../Utils/Storage';
import { useAppToast } from '../Components/Toast';
import { normalizeCard, fetchPassportBlob, getPublicCardApi } from '../Utils/ApiCall';
import { useAuth } from '../Utils/AuthContext';
import ThemeToggle from '../Components/ThemeToggle';
import { getInitials } from '../Utils/Helpers';
import { useCompany } from '../Utils/CompanyContext';

/**
 * Standard Executive Contact Card with direct action link and 1-tap copy.
 */
const ContactCard: React.FC<{
  icon: React.ReactElement;
  label: string;
  sublabel: string;
  href: string;
  isExternal?: boolean;
  valueToCopy: string;
}> = ({ icon, label, sublabel, href, isExternal, valueToCopy }) => {
  const [copied, setCopied] = useState(false);
  const toast = useAppToast();
  const cardBg = useColorModeValue('rgba(13, 31, 76, 0.025)', 'rgba(255, 255, 255, 0.04)');
  const cardBorder = useColorModeValue('rgba(13, 31, 76, 0.07)', 'rgba(255, 255, 255, 0.07)');
  const cardHoverBorder = useColorModeValue('#1565C0', '#00B4D8');
  const cardHoverBg = useColorModeValue('rgba(18, 100, 232, 0.05)', 'rgba(255, 255, 255, 0.07)');
  const titleColor = useColorModeValue('#0F172A', '#F8FAFC');
  const sublabelColor = useColorModeValue('gray.500', 'gray.400');
  const badgeBg = useColorModeValue('#EEF4FF', 'rgba(0, 180, 216, 0.12)');
  const badgeColor = useColorModeValue('#0E398C', '#00B4D8');
  const copyBtnColor = useColorModeValue('gray.400', 'gray.500');
  const copyBtnHoverColor = useColorModeValue('#0F172A', 'white');

  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(valueToCopy);
    setCopied(true);
    toast({
      title: 'Copied to clipboard',
      description: `${sublabel}: ${valueToCopy}`,
      status: 'success',
      duration: 1800,
    });
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Flex
      as="a"
      href={href}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      align="center"
      justify="space-between"
      p={3}
      borderRadius="14px"
      bg={cardBg}
      border="1px solid"
      borderColor={cardBorder}
      transition="all 0.18s ease"
      _hover={{
        bg: cardHoverBg,
        borderColor: cardHoverBorder,
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(13, 31, 76, 0.06)',
      }}
      textDecoration="none"
      cursor="pointer"
      role="group"
    >
      <HStack spacing={3} minW={0} flex={1}>
        <Center
          boxSize="36px"
          borderRadius="10px"
          bg={badgeBg}
          color={badgeColor}
          flexShrink={0}
          transition="all 0.18s ease"
          _groupHover={{ transform: 'scale(1.05)' }}
        >
          {icon}
        </Center>
        <Box minW={0} flex={1}>
          <Text fontSize="13px" fontWeight={600} color={titleColor} isTruncated>
            {label}
          </Text>
          <Text
            fontSize="9.5px"
            fontWeight={700}
            color={sublabelColor}
            textTransform="uppercase"
            letterSpacing="0.06em"
            mt="1px"
          >
            {sublabel}
          </Text>
        </Box>
      </HStack>

      <Tooltip label={copied ? 'Copied!' : 'Copy'} placement="left" hasArrow>
        <IconButton
          aria-label={`Copy ${sublabel}`}
          icon={copied ? <CheckIcon color="green.400" boxSize="11px" /> : <CopyIcon boxSize="13px" />}
          size="xs"
          boxSize="28px"
          variant="ghost"
          borderRadius="full"
          color={copied ? 'green.400' : copyBtnColor}
          _hover={{ color: copyBtnHoverColor, bg: 'whiteAlpha.200' }}
          onClick={handleCopy}
          flexShrink={0}
        />
      </Tooltip>
    </Flex>
  );
};

const CardPreview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useAppToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [card, setCard] = useState<BusinessCard | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const qrRef = useRef<HTMLDivElement>(null);
  const { isAuthenticated } = useAuth();
  const { company } = useCompany();

  // All useColorModeValue hooks hoisted to top before any return:
  const pageBg = useColorModeValue(
    'radial-gradient(circle at 50% 0%, #EBF2FE 0%, #F5F7FB 40%, #EEF2F8 100%)',
    'radial-gradient(circle at 50% 0%, #0D1C3C 0%, #060B18 50%, #040812 100%)'
  );
  const cardSurface = useColorModeValue('#FFFFFF', '#0C1527');
  const cardBorder = useColorModeValue('rgba(13, 31, 76, 0.08)', 'rgba(255, 255, 255, 0.09)');
  const cardShadow = useColorModeValue(
    '0 20px 40px -12px rgba(13, 31, 76, 0.12), 0 4px 16px rgba(13, 31, 76, 0.04)',
    '0 24px 48px -12px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(255, 255, 255, 0.08)'
  );
  const nameColor = useColorModeValue('#0B132B', '#F8FAFC');
  const roleColor = useColorModeValue('#1E293B', '#E2E8F0');
  const deptColor = useColorModeValue('#475569', '#94A3B8');
  const dividerColor = useColorModeValue('rgba(13, 31, 76, 0.07)', 'rgba(255, 255, 255, 0.08)');
  const emptyBg = useColorModeValue('#F4F6FB', '#070C18');
  const topBtnBg = useColorModeValue('white', 'rgba(255, 255, 255, 0.08)');
  const topBtnBorder = useColorModeValue('rgba(13, 31, 76, 0.1)', 'rgba(255, 255, 255, 0.1)');
  const topBtnColor = useColorModeValue('#0F172A', 'white');
  const subtextColor = useColorModeValue('gray.600', 'gray.400');
  const mutedTextColor = useColorModeValue('gray.500', 'gray.400');
  const avatarRingBg = useColorModeValue('white', '#0D1527');
  const avatarRingBorder = useColorModeValue('white', '#0D1527');
  const avatarFallbackBg = useColorModeValue('#EEF4FF', '#16233F');
  const unitBadgeBg = useColorModeValue('rgba(13, 31, 76, 0.05)', 'whiteAlpha.100');
  const unitBadgeColor = useColorModeValue('gray.700', 'gray.300');
  const outlineBtnBorder = useColorModeValue('rgba(13, 31, 76, 0.15)', 'rgba(255, 255, 255, 0.15)');
  const outlineBtnBg = useColorModeValue('rgba(13, 31, 76, 0.02)', 'whiteAlpha.050');
  const outlineBtnColor = useColorModeValue('#0F172A', 'white');
  const outlineBtnHoverBg = useColorModeValue('rgba(13, 31, 76, 0.06)', 'whiteAlpha.150');
  const outlineBtnHoverBorder = useColorModeValue('rgba(13, 31, 76, 0.3)', 'rgba(255, 255, 255, 0.3)');
  const modalBg = useColorModeValue('white', '#0D1527');
  const modalBorder = useColorModeValue('gray.100', 'whiteAlpha.150');
  const modalTitleColor = useColorModeValue('#0F172A', 'white');
  const watermarkBg = useColorModeValue('rgba(13, 31, 76, 0.03)', 'rgba(0, 0, 0, 0.25)');
  const insetCardBg = useColorModeValue('rgba(13, 31, 76, 0.025)', 'rgba(255, 255, 255, 0.03)');
  const insetCardBorder = useColorModeValue('rgba(13, 31, 76, 0.07)', 'rgba(255, 255, 255, 0.07)');
  const pendingBannerBg = useColorModeValue('rgba(255, 183, 3, 0.1)', 'rgba(255, 183, 3, 0.08)');
  const pendingBannerBorder = useColorModeValue('rgba(255, 183, 3, 0.3)', 'rgba(255, 183, 3, 0.25)');
  const pendingBannerText = useColorModeValue('#B45309', '#FFDC21');

  useEffect(() => {
    if (id) {
      getPublicCardApi(id)
        .then(async (res) => {
          if (res.data.success && res.data.data) {
            const normalized = normalizeCard(res.data.data);
            setCard(normalized);
            const lookupKey = res.data.data._id || res.data.data.id;
            if (lookupKey) {
              const blobUrl = await fetchPassportBlob(lookupKey);
              if (blobUrl) setPhotoUrl(blobUrl);
            }
          } else {
            const data = storage.getCardById(id);
            if (data) setCard(data);
          }
        })
        .catch((err) => {
          console.error('CardPreview: API Error', err);
          const data = storage.getCardById(id);
          if (data) setCard(data);
        });
    }
  }, [id]);

  const downloadQRCode = () => {
    const canvas = qrRef.current?.querySelector('canvas') as HTMLCanvasElement;
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = url;
      link.download = `ekopower-qr-${card?.name ? card.name.replace(/\s+/g, '_') : 'card'}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast({
        title: 'QR Code downloaded',
        description: 'The high-resolution QR code is saved.',
        status: 'success',
        duration: 2500,
      });
    }
  };

  const copyCardLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast({
      title: 'Link copied to clipboard',
      description: 'Share this link with anyone to view your digital card.',
      status: 'success',
      duration: 2500,
    });
  };

  const downloadVCard = () => {
    if (!card) return;
    const vCard = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `FN:${card.name}`,
      `ORG:${card.company || company.cardOrgName}`,
      `TITLE:${card.role}`,
      `ADR;TYPE=WORK:;;;${card.department};${card.unit};;`,
      `TEL;TYPE=CELL:${card.phone}`,
      card.personalTel ? `TEL;TYPE=HOME:${card.personalTel}` : '',
      `EMAIL;TYPE=WORK:${card.email}`,
      `URL:${card.website}`,
      'END:VCARD',
    ]
      .filter(Boolean)
      .join('\n');

    const blob = new Blob([vCard], { type: 'text/vcard;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${card.name.replace(/\s+/g, '_')}.vcf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast({
      title: 'Contact saved',
      description: 'vCard file downloaded to your device contacts.',
      status: 'success',
      duration: 2500,
    });
  };

  if (!card) {
    return (
      <Center minH="100vh" bg={emptyBg}>
        <VStack spacing={4}>
          <Image src={company.logo} h="32px" alt={company.companyName} objectFit="contain" />
          <Text color={mutedTextColor} fontSize="14px">Looking for digital card...</Text>
        </VStack>
      </Center>
    );
  }

  const currentUrl = window.location.href;
  const isApproved =
    card.status?.toLowerCase() === 'approved' ||
    card.status?.toLowerCase() === 'active';
  const websiteUrl = card.website?.startsWith('http')
    ? card.website
    : `https://${card.website || company.emailDomain}`;

  return (
    <Flex
      bg={pageBg}
      minH="100vh"
      w="full"
      py={{ base: 4, sm: 8 }}
      px={4}
      fontFamily="'Inter', sans-serif"
      transition="background 0.3s ease"
      direction="column"
      justify="center"
      align="center"
    >
      <Box w="full" maxW="380px" mx="auto">

        {/* Top Control Bar */}
        <Flex mb={3} align="center" justify="space-between" px={1} w="full">
          {isAuthenticated ? (
            <IconButton
              aria-label="Back to dashboard"
              icon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              size="sm"
              bg={topBtnBg}
              border="1px solid"
              borderColor={topBtnBorder}
              borderRadius="full"
              color={topBtnColor}
              boxShadow="sm"
              _hover={{ transform: 'translateX(-2px)' }}
              transition="all 0.2s"
            />
          ) : (
            <HStack spacing={2}>
              <Box w="6px" h="6px" borderRadius="full" bg="green.400" />
              <Text fontSize="11px" fontWeight={600} color={subtextColor} letterSpacing="0.04em">
                {company.portalTitle}
              </Text>
            </HStack>
          )}

          {/* Right utilities: Theme Switcher & QR Button (Only when approved) */}
          <HStack spacing={2}>
            <ThemeToggle variant="segmented" size="sm" />

            {isApproved && (
              <Tooltip label="Scan QR Code" placement="bottom" hasArrow>
                <IconButton
                  aria-label="Share QR code"
                  icon={<Icon as={MdQrCode2 as any} boxSize="17px" />}
                  size="sm"
                  bg={topBtnBg}
                  border="1px solid"
                  borderColor={topBtnBorder}
                  borderRadius="full"
                  color={topBtnColor}
                  boxShadow="sm"
                  onClick={onOpen}
                  _hover={{ transform: 'scale(1.05)' }}
                  transition="all 0.2s"
                />
              </Tooltip>
            )}
          </HStack>
        </Flex>

        {/* ============================================================ */}
        {/*  STANDARD EXECUTIVE DIGITAL CARD (CENTRALIZED)               */}
        {/* ============================================================ */}
        <Box
          bg={cardSurface}
          borderRadius="22px"
          border="1px solid"
          borderColor={cardBorder}
          boxShadow={cardShadow}
          overflow="hidden"
          position="relative"
          transition="all 0.25s ease"
          w="full"
        >
          {/* Top Multi-Color Brand Energy Accent Line */}
          <Box
            h="3.5px"
            w="full"
            bg="linear-gradient(90deg, #00B4D8 0%, #1565C0 35%, #FFB703 70%, #F35B04 100%)"
          />

          {/* Luxury Executive Header Banner */}
          <Flex
            bg="linear-gradient(135deg, #050D21 0%, #092258 55%, #0E3E9C 100%)"
            px={4}
            py={3}
            align="center"
            justify="space-between"
            position="relative"
            overflow="hidden"
          >
            {/* Ambient decorative energy glow */}
            <Box
              position="absolute"
              top="-25px"
              right="-25px"
              w="100px"
              h="100px"
              borderRadius="full"
              bg="radial-gradient(circle, rgba(0, 180, 216, 0.25) 0%, transparent 70%)"
              pointerEvents="none"
            />

            {/* Elevated Logo Capsule */}
            <Box
              bg="rgba(255, 255, 255, 0.96)"
              borderRadius="9px"
              px={3}
              py={1.5}
              boxShadow="0 2px 8px rgba(0, 0, 0, 0.2)"
              border="1px solid rgba(255, 255, 255, 0.6)"
            >
              <Image
                src={company.logo}
                h="20px"
                alt={company.companyName}
                objectFit="contain"
              />
            </Box>

            {/* Verified / Pending Status Badge */}
            <HStack
              spacing={1.5}
              bg={isApproved ? "rgba(255, 255, 255, 0.12)" : "rgba(255, 183, 3, 0.18)"}
              backdropFilter="blur(8px)"
              px={2.5}
              py={1}
              borderRadius="full"
              border={`1px solid ${isApproved ? "rgba(255, 255, 255, 0.2)" : "rgba(255, 183, 3, 0.4)"}`}
            >
              <Icon as={MdVerified as any} color={isApproved ? "#00B4D8" : "#FFDC21"} boxSize="12px" />
              <Text
                fontFamily="'Inter', sans-serif"
                fontSize="9.5px"
                fontWeight={700}
                color={isApproved ? "white" : "#FFDC21"}
                letterSpacing="0.08em"
                textTransform="uppercase"
              >
                {isApproved ? 'Verified' : 'Pending Approval'}
              </Text>
            </HStack>
          </Flex>

          {/* Profile Identity Row */}
          <Box px={4} pt={4} pb={3}>
            <HStack align="center" spacing={3.5}>
              {/* Avatar with Metallic Halo */}
              <Box
                boxSize="68px"
                borderRadius="full"
                p="2px"
                bg="linear-gradient(135deg, #00B4D8 0%, #FFB703 50%, #F35B04 100%)"
                boxShadow="0 4px 14px -2px rgba(9, 26, 66, 0.25)"
                flexShrink={0}
              >
                <Box
                  boxSize="full"
                  borderRadius="full"
                  overflow="hidden"
                  bg={avatarRingBg}
                  border="2px solid"
                  borderColor={avatarRingBorder}
                >
                  {photoUrl ? (
                    <Image
                      src={photoUrl}
                      alt={card.name}
                      boxSize="full"
                      objectFit="cover"
                    />
                  ) : (
                    <Center boxSize="full" bg={avatarFallbackBg}>
                      <Text
                        fontSize="18px"
                        fontWeight={700}
                        color="brand.500"
                        fontFamily="'Inter', sans-serif"
                      >
                        {getInitials(card.name)}
                      </Text>
                    </Center>
                  )}
                </Box>
              </Box>

              {/* Names & Titles */}
              <VStack align="flex-start" spacing={0.5} minW={0} flex={1}>
                <Text
                  fontFamily="'Cormorant Garamond', Georgia, serif"
                  fontSize={{ base: "22px", md: "24px" }}
                  fontWeight={500}
                  color={nameColor}
                  letterSpacing="-0.2px"
                  textTransform="capitalize"
                  lineHeight={1.15}
                  isTruncated
                  w="full"
                >
                  {card.name}
                </Text>

                <Text
                  fontSize="11px"
                  fontWeight={700}
                  color={roleColor}
                  textTransform="uppercase"
                  letterSpacing="0.07em"
                  lineHeight={1.2}
                  isTruncated
                  w="full"
                >
                  {card.role}
                </Text>

                <Text
                  fontSize="11px"
                  fontWeight={500}
                  color={deptColor}
                  lineHeight={1.2}
                  isTruncated
                  w="full"
                >
                  {card.department}
                </Text>

                {/* Inline Unit & Company badges */}
                <HStack spacing={1.5} pt={0.5} flexWrap="wrap">
                  {card.unit && (
                    <Badge
                      fontSize="9px"
                      fontWeight={600}
                      px={1.5}
                      py="1px"
                      borderRadius="md"
                      bg={unitBadgeBg}
                      color={unitBadgeColor}
                      letterSpacing="0.02em"
                    >
                      {card.unit}
                    </Badge>
                  )}

                  <HStack
                    spacing={1}
                    bg="linear-gradient(135deg, #071536 0%, #0E3585 100%)"
                    px={2}
                    py="1px"
                    borderRadius="full"
                  >
                    <Box w="4px" h="4px" borderRadius="full" bg={company.colors.accent} />
                    <Text
                      fontSize="9px"
                      fontWeight={700}
                      color="white"
                      textTransform="uppercase"
                      letterSpacing="0.08em"
                    >
                      {card.company || company.shortName}
                    </Text>
                  </HStack>
                </HStack>
              </VStack>
            </HStack>
          </Box>

          {/* Standard Contact Cards (Replaces the stripped quick actions with clean standard cards) */}
          <Box px={4} pb={3}>
            <VStack spacing={2} align="stretch">
              <ContactCard
                icon={<Icon as={MdEmail as any} boxSize="17px" />}
                label={card.email}
                sublabel="Work Email"
                href={`mailto:${card.email}`}
                valueToCopy={card.email}
              />
              <ContactCard
                icon={<Icon as={MdPhone as any} boxSize="17px" />}
                label={card.personalTel ? `${card.phone} / ${card.personalTel}` : card.phone}
                sublabel="Mobile Phone"
                href={`tel:${card.phone}`}
                valueToCopy={card.phone}
              />
              <ContactCard
                icon={<Icon as={MdOutlineWeb as any} boxSize="18px" />}
                label={card.website || 'https://ekopowerlimited.com'}
                sublabel="Official Website"
                href={websiteUrl}
                isExternal
                valueToCopy={card.website || 'https://ekopowerlimited.com'}
              />
            </VStack>
          </Box>

          {/* Action Hub */}
          <Box px={4} pb={3.5}>
            {!isApproved && (
              <Box
                mb={3}
                px={3}
                py={2.5}
                bg={pendingBannerBg}
                border="1px solid"
                borderColor={pendingBannerBorder}
                borderRadius="12px"
                textAlign="center"
              >
                <Text fontSize="11px" fontWeight={600} color={pendingBannerText}>
                  ⏳ Pending Approval — QR code and full verification will become available once approved by your supervisor.
                </Text>
              </Box>
            )}

            <HStack spacing={2} w="full">
              {/* Primary Save Contact Button */}
              <Button
                flex={1}
                h="44px"
                bg="linear-gradient(135deg, #0B2C6D 0%, #1255C8 100%)"
                color="white"
                borderRadius="12px"
                fontSize="13.5px"
                fontWeight={600}
                letterSpacing="0.02em"
                leftIcon={<Icon as={MdDownload as any} boxSize="17px" />}
                boxShadow="0 4px 14px rgba(18, 85, 200, 0.28)"
                _hover={{
                  bg: 'linear-gradient(135deg, #0E398C 0%, #1664EC 100%)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 6px 18px rgba(18, 85, 200, 0.38)',
                }}
                _active={{ transform: 'translateY(0)' }}
                onClick={downloadVCard}
                transition="all 0.15s ease"
              >
                Save Contact (.vcf)
              </Button>

              {/* QR Code Action Button - ONLY VISIBLE WHEN APPROVED */}
              {isApproved && (
                <Tooltip label="View QR Code" placement="top" hasArrow>
                  <IconButton
                    aria-label="QR Code"
                    icon={<Icon as={MdQrCode2 as any} boxSize="18px" />}
                    h="44px"
                    w="44px"
                    borderRadius="12px"
                    variant="outline"
                    borderColor={outlineBtnBorder}
                    bg={outlineBtnBg}
                    color={outlineBtnColor}
                    _hover={{
                      bg: outlineBtnHoverBg,
                      borderColor: outlineBtnHoverBorder,
                      transform: 'translateY(-1px)',
                    }}
                    onClick={onOpen}
                  />
                </Tooltip>
              )}

              {/* Copy Link Button */}
              <Tooltip label="Copy Card Link" placement="top" hasArrow>
                <IconButton
                  aria-label="Copy Card Link"
                  icon={<CopyIcon boxSize="14px" />}
                  h="44px"
                  w="44px"
                  borderRadius="12px"
                  variant="outline"
                  borderColor={outlineBtnBorder}
                  bg={outlineBtnBg}
                  color={outlineBtnColor}
                  _hover={{
                    bg: outlineBtnHoverBg,
                    borderColor: outlineBtnHoverBorder,
                    transform: 'translateY(-1px)',
                  }}
                  onClick={copyCardLink}
                />
              </Tooltip>
            </HStack>
          </Box>

          {/* Footer watermark branding */}
          <Box
            py={2.5}
            textAlign="center"
            bg={watermarkBg}
            borderTop="1px solid"
            borderColor={dividerColor}
          >
            <Text
              fontSize="9.5px"
              fontWeight={600}
              color={mutedTextColor}
              letterSpacing="0.04em"
            >
              Powered by {company.companyName} • Digital Identity
            </Text>
          </Box>
        </Box>

        {/* QR Code Modal - ONLY ACCESSIBLE IF APPROVED */}
        {isApproved && (
          <Modal isOpen={isOpen} onClose={onClose} isCentered motionPreset="slideInBottom">
            <ModalOverlay backdropFilter="blur(8px)" bg="blackAlpha.600" />
            <ModalContent
              borderRadius="22px"
              mx={4}
              p={1.5}
              bg={modalBg}
              border="1px solid"
              borderColor={modalBorder}
              boxShadow="0 25px 60px rgba(0, 0, 0, 0.4)"
            >
              <ModalHeader textAlign="center" pt={5} pb={1}>
                <Text
                  fontFamily="'Cormorant Garamond', Georgia, serif"
                  fontSize="24px"
                  fontWeight={500}
                  color={modalTitleColor}
                >
                  Scan to Connect
                </Text>
                <Text fontSize="11.5px" color={mutedTextColor} fontWeight={400} mt={0.5}>
                  Instant access to {card.name}'s verified card
                </Text>
              </ModalHeader>
              <ModalCloseButton top={4} right={4} borderRadius="full" size="sm" />

              <ModalBody pb={5}>
                <VStack spacing={4}>
                  {/* QR Container */}
                  <Box
                    p={4}
                    bg="white"
                    borderRadius="16px"
                    boxShadow="0 8px 24px rgba(0, 0, 0, 0.08)"
                    border="1px solid"
                    borderColor="gray.100"
                    ref={qrRef}
                  >
                    <QRCodeCanvas
                      value={currentUrl}
                      size={200}
                      level="H"
                      includeMargin
                    />
                  </Box>

                  <Text
                    fontSize="11.5px"
                    color={subtextColor}
                    textAlign="center"
                    lineHeight={1.5}
                    px={3}
                  >
                    Point any phone camera to scan and automatically add contact to address book.
                  </Text>

                  <HStack w="full" spacing={2.5}>
                    <Button
                      flex={1}
                      h="40px"
                      bg="linear-gradient(135deg, #0B2C6D 0%, #1255C8 100%)"
                      color="white"
                      borderRadius="10px"
                      fontSize="12.5px"
                      fontWeight={600}
                      leftIcon={<Icon as={MdDownload as any} />}
                      _hover={{ opacity: 0.9, transform: 'translateY(-1px)' }}
                      onClick={downloadQRCode}
                    >
                      Download QR
                    </Button>

                    <Button
                      flex={1}
                      h="40px"
                      variant="outline"
                      borderRadius="10px"
                      fontSize="12.5px"
                      fontWeight={600}
                      leftIcon={<CopyIcon />}
                      onClick={copyCardLink}
                    >
                      Copy Link
                    </Button>
                  </HStack>
                </VStack>
              </ModalBody>
            </ModalContent>
          </Modal>
        )}

      </Box>
    </Flex>
  );
};

export default CardPreview;